import moment from 'moment';

import {
  CalendarAssignmentEmployee,
  DaySchedule,
  EmployeesSchedules,
  PersonAbsence,
  Shift,
  ShiftInterfere,
  TimelineItem,
  TimelineScale
} from '../../types/interfaces';

import { createTaskIdGenerator, formatScheduleTitle, getDayBackgroundColor, shiftToDaySchedule } from './helpers';

export interface BuildEmployeeScheduleItemsArgs {
  employeesShifts: EmployeesSchedules | null;
  newShifts: Shift[];
  updatedShifts: Shift[];
  deletedShiftIds: string[];
  simulatedDeletedShifts: string[];
  simulatedUpdatedShifts: Shift[];
  employees: { id: number }[] | undefined;
  timelineScale: TimelineScale;
}

export const buildEmployeeScheduleItems = (
  {
    employeesShifts,
    newShifts,
    updatedShifts,
    deletedShiftIds,
    simulatedDeletedShifts,
    simulatedUpdatedShifts,
    employees,
    timelineScale
  }: BuildEmployeeScheduleItemsArgs
) => {
  const employeeScheduleItems: TimelineItem[] = [];
  const allShifts: Record<string, Record<string, DaySchedule[]>> = JSON.parse(
    JSON.stringify(employeesShifts || {})
  );

  [...newShifts, ...updatedShifts, ...simulatedUpdatedShifts].forEach(
    (shift) => {
      if (!deletedShiftIds.includes(shift.guid)) {
        const personId = shift.personId.toString();
        const date = shift.date;
        if (!allShifts[personId]) allShifts[personId] = {};
        if (!allShifts[personId][date]) allShifts[personId][date] = [];
        const index = allShifts[personId][date].findIndex((s) => s.guid === shift.guid);
        if (index !== -1) {
          allShifts[personId][date][index] = shiftToDaySchedule(shift);
        } else {
          allShifts[personId][date].push(shiftToDaySchedule(shift));
        }
      }
    }
  );

  (employees || []).forEach((emp) => {
    const empShiftsByDate = allShifts[emp.id.toString()] ?? {};
    Object.entries(empShiftsByDate).forEach(([dateKey, shiftArray]) => {
      const dayMoment = moment(dateKey, 'YYYY-MM-DD');

      const shiftsToDisplay = shiftArray
        .filter(
          (s) => !deletedShiftIds.includes(s.guid) && !simulatedDeletedShifts.includes(s.guid)
        )
        .map((s) => ({
          schedule: s,
          className: s.isTemplate ? 'template-shift' : 'manual-shift'
        }));

      const deletedNonTemplatedShifts = shiftArray.filter(
        (s) => (deletedShiftIds.includes(s.guid) || simulatedDeletedShifts.includes(s.guid)) && !s.guid.startsWith('temp-')
      );

      const deletedShiftsToDisplay = deletedNonTemplatedShifts.map((s) => ({
        schedule: s,
        className: 'deleted-shift'
      }));

      const allShiftsToDisplay = [...shiftsToDisplay, ...deletedShiftsToDisplay];

      allShiftsToDisplay.forEach(({ schedule, className }, index) => {
        let itemStart: number;
        let itemEnd: number;

        if (timelineScale === TimelineScale.MONTH) {
          const totalShifts = allShiftsToDisplay.length;
          if (totalShifts === 1) {
            itemStart = dayMoment.clone().add(2, 'hours').valueOf();
            itemEnd = dayMoment.clone().add(1, 'day').subtract(2, 'hours').valueOf();
          } else {
            const dayStart = dayMoment.clone().startOf('day').valueOf();
            const dayEnd = dayMoment.clone().endOf('day').valueOf();
            const shiftWidth = (dayEnd - dayStart) / totalShifts;
            itemStart = dayStart + index * shiftWidth;
            itemEnd = itemStart + shiftWidth;
          }
        } else {
          const startMoment = moment.utc(schedule.start).local();
          const endMoment = moment.utc(schedule.end).local();
          itemStart = startMoment.valueOf();
          itemEnd = endMoment.valueOf();
        }

        employeeScheduleItems.push({
          id: schedule.guid ?? `${emp.id}-shift-${dateKey}`,
          guid: schedule.guid ?? `${emp.id}-shift-${dateKey}`,
          group: emp.id,
          className: `filled-cell ${className}`,
          title: formatScheduleTitle(schedule, timelineScale),
          start_time: itemStart,
          end_time: itemEnd,
          itemProps: {
            style: { color: '#000000', borderRadius: 4, opacity: 0.9, top: 0, fontSize: 'clamp(9px, calc(9px + 3 * ((100vw - 1450px) / (1800 - 1450))), 12px)' }
          }
        });
      });
    });
  });

  return employeeScheduleItems;
};

export const buildTimesheetShiftItems = (
  employeesTimesheet: Record<string, Record<string, number>>,
  timelineScale: TimelineScale
) => {
  const timesheetItems: TimelineItem[] = [];

  Object.entries(employeesTimesheet).forEach(([personIdStr, dateHours]) => {
    const personId = parseInt(personIdStr);
    Object.entries(dateHours).forEach(([dateKey, hours]) => {
      if (hours > 0) {
        const dayMoment = moment(dateKey, 'YYYY-MM-DD');
        const allTimesheetItemsForDay = Object.entries(dateHours)
          .filter(([d]) => d === dateKey)
          .map(([, h]) => h).length;

        let itemStart: number;
        let itemEnd: number;

        if (timelineScale === TimelineScale.MONTH) {
          if (allTimesheetItemsForDay === 1) {
            itemStart = dayMoment.clone().add(2, 'hours').valueOf();
            itemEnd = dayMoment.clone().add(1, 'day').subtract(2, 'hours').valueOf();
          } else {
            const dayStart = dayMoment.clone().startOf('day').valueOf();
            const dayEnd = dayMoment.clone().endOf('day').valueOf();
            const shiftWidth = (dayEnd - dayStart) / allTimesheetItemsForDay;
            const index = Object.keys(dateHours)
              .filter((d) => moment(d, 'YYYY-MM-DD').isSame(dateKey, 'day'))
              .indexOf(dateKey);
            itemStart = dayStart + index * shiftWidth;
            itemEnd = itemStart + shiftWidth;
          }
        } else {
          itemStart = dayMoment.clone().valueOf();
          itemEnd = dayMoment.clone().add(1, 'day').valueOf();
        }

        const guid = `timesheet-${personId}-${dateKey}`;

        timesheetItems.push({
          id: guid,
          guid: guid,
          group: personId,
          className: 'filled-cell manual-shift',
          title: `${hours.toFixed(1)}`,
          start_time: itemStart,
          end_time: itemEnd,
          itemProps: {
            style: { color: '#000000', borderRadius: 4, opacity: 0.9, top: 0, fontSize: 'clamp(9px, calc(9px + 3 * ((100vw - 1450px) / (1800 - 1450))), 12px)' }
          }
        });
      }
    });
  });

  return timesheetItems;
};

export const buildTaskItemsMonthScale = (
  notClosedTask: boolean,
  closedTask: boolean,
  computeTasksByDay: Record<string, Record<string, { count: number; tasks: any[] }>>,
  visibleTimeStart: number,
  visibleTimeEnd: number
) => {
  if (!notClosedTask && !closedTask) return [] as TimelineItem[];
  const idGen = createTaskIdGenerator();
  const items: TimelineItem[] = [];

  Object.entries(computeTasksByDay).forEach(([personIdStr, dayMap]) => {
    Object.entries(dayMap).forEach(([dayStr, data]) => {
      let filteredTasks = data.tasks;
      
      if (notClosedTask && closedTask) {
        filteredTasks = data.tasks;
      } else if (notClosedTask) {
        filteredTasks = data.tasks.filter(task => !task.isClosed);
      } else if (closedTask) {
        filteredTasks = data.tasks.filter(task => task.isClosed);
      }
      
      if (
        filteredTasks.length > 0 &&
        moment(dayStr).isBetween(moment(visibleTimeStart), moment(visibleTimeEnd), null, '[]')
      ) {
        const dayMoment = moment(dayStr);
        items.push({
          id: idGen(),
          group: Number(personIdStr),
          title: `Нар: ${filteredTasks.length}`,
          start_time: dayMoment.clone().add(2, 'hours').valueOf(),
          end_time: dayMoment.clone().add(1, 'day').subtract(2, 'hours').valueOf(),
          className: 'task-item',
          itemProps: { 
            style: { 
              fontSize: 'clamp(9px, calc(9px + 3 * ((100vw - 1450px) / (1800 - 1450))), 12px)', 
              borderRadius: 4, 
              color: '#000', 
              top: 0 
            } 
          },
          data: { 
            type: 'task', 
            personId: Number(personIdStr), 
            day: dayStr, 
            tasks: filteredTasks
          }
        } as any);
      }
    });
  });
  return items;
};

export const buildDepartmentItems = (
  dayMoment: moment.Moment,
  schedules: DaySchedule[],
  timelineScale: TimelineScale,
  DEPARTMENT_SCHEDULE_GROUP_ID: number,
  generateId: () => string
) => {
  const departmentItems: any[] = [];
  schedules.forEach((schedule) => {
    const startMoment = moment.utc(schedule.start).local();
    const endMoment = moment.utc(schedule.end).local();

    let itemStart: number, itemEnd: number;
    if (timelineScale === TimelineScale.MONTH) {
      itemStart = dayMoment.clone().add(2, 'hours').valueOf();
      itemEnd = dayMoment.clone().add(1, 'day').subtract(2, 'hours').valueOf();
    } else {
      itemStart = startMoment.valueOf();
      itemEnd = endMoment.valueOf();
    }

    departmentItems.push({
      id: generateId(),
      className: 'department-schedule-item',
      group: DEPARTMENT_SCHEDULE_GROUP_ID,
      title: formatScheduleTitle(schedule, timelineScale),
      start_time: itemStart,
      end_time: itemEnd,
      itemProps: {
        style: {
          backgroundColor: '#d4edda',
          border: '1px solid #5b9bd5',
          color: '#000000',
          borderRadius: 4,
          opacity: 0.9,
          cursor: 'default',
          pointerEvents: 'none',
          top: 0,
          fontSize: 'clamp(9px, calc(9px + 3 * ((100vw - 1450px) / (1800 - 1450))), 12px)'
        }
      }
    });
  });
  return departmentItems;
};

export const buildDepartmentBgItem = (
  dayMoment: moment.Moment,
  dayType: any,
  DEPARTMENT_SCHEDULE_GROUP_ID: number,
  id: number
) => ({
  id,
  className: getDayBackgroundColor(dayType),
  group: DEPARTMENT_SCHEDULE_GROUP_ID,
  title: '',
  start_time: dayMoment.valueOf(),
  end_time: dayMoment.clone().add(1, 'day').valueOf(),
  itemProps: {
    style: { borderRadius: 4, opacity: 0.8, cursor: 'default', pointerEvents: 'none', top: 0 }
  }
});

export const buildEmployeeBgItem = (
  empId: number,
  dayMoment: moment.Moment,
  dayType: any
) => ({
  id: `${empId}-bg-${dayMoment.format('YYYY-MM-DD')}`,
  className: getDayBackgroundColor(dayType),
  group: empId,
  title: '',
  titleStyle: { fontSize: '18px', textAlign: 'center', display: 'block', marginTop: '-3px' },
  start_time: dayMoment.valueOf(),
  end_time: dayMoment.clone().add(1, 'day').valueOf(),
  itemProps: { style: { minHeight: 40, borderRadius: 4, opacity: 0.8, top: 0 } }
});

export const buildAbsenceItems = (
  calendarAssignment: any,
  employeesAbsences: Record<string, Record<string, PersonAbsence[]>>,
  newAbsences: PersonAbsence[],
  updatedAbsences: PersonAbsence[],
  deletedAbsenceKeys: any[],
  timelineScale: TimelineScale
) => {
  const absenceItems: any[] = [];
  const allAbsences: Record<string, Record<string, PersonAbsence[]>> = JSON.parse(
    JSON.stringify(employeesAbsences || {})
  );

  [...newAbsences, ...updatedAbsences].forEach((abs) => {
    const personId = abs.personId.toString();
    if (!allAbsences[personId]) allAbsences[personId] = {};
    if (!allAbsences[personId][abs.date]) allAbsences[personId][abs.date] = [];
    const index = allAbsences[personId][abs.date].findIndex(
      (a) => a.from === abs.from && a.to === abs.to
    );
    if (index !== -1) {
      allAbsences[personId][abs.date][index] = abs;
    } else {
      allAbsences[personId][abs.date].push(abs);
    }
  });

  calendarAssignment?.employees.forEach((emp: any) => {
    const empAbsencesByDate = allAbsences[emp.id.toString()] ?? {};
    Object.entries(empAbsencesByDate).forEach(([dateKey, absenceArray]) => {
      const absencesToDisplay = (absenceArray as PersonAbsence[]).filter((abs) => {
        const isDeleted = deletedAbsenceKeys.some(
          (k) => ('id' in k && (k as any).id === abs.id) || ('guid' in k && (k as any).guid === abs.guid)
        );
        if (isDeleted) return true;
        if (abs.id) {
          return !deletedAbsenceKeys.some((k) => 'id' in k && (k as any).id === abs.id) && !((updatedAbsences?.some((k) => 'id' in k && (k as any).id === abs.id && !(abs as any).updated)) ?? false);
        }
        return !deletedAbsenceKeys.some((k) => 'guid' in k && (k as any).guid === abs.guid) && !((updatedAbsences?.some((k) => 'guid' in k && (k as any).guid === abs.guid && !(abs as any).updated)) ?? false);
      });

      const absencesByStartDate = (absencesToDisplay as PersonAbsence[]).reduce(
        (acc: { [key: string]: PersonAbsence[] }, ab: PersonAbsence) => {
          const startDate = moment(ab.from).startOf('day').format('YYYY-MM-DD');
          if (!acc[startDate]) acc[startDate] = [];
          acc[startDate].push(ab);
          return acc;
        },
        {}
      );

      const absencesByDay: Record<string, PersonAbsence[]> = {};
      if (timelineScale === TimelineScale.MONTH) {
        (absencesToDisplay as PersonAbsence[]).forEach((ab) => {
          const startMoment = moment(ab.from);
          const endMoment = moment(ab.to);
          const currentDate = startMoment.clone().startOf('day');
          const endDate = endMoment.clone().startOf('day');
          while (currentDate.isSameOrBefore(endDate)) {
            const dKey = currentDate.format('YYYY-MM-DD');
            if (!absencesByDay[dKey]) absencesByDay[dKey] = [];
            if (!absencesByDay[dKey].some((a) => a.from === ab.from && a.to === ab.to)) {
              absencesByDay[dKey].push(ab);
            }
            currentDate.add(1, 'day');
          }
        });
        Object.keys(absencesByDay).forEach((dayKey) => {
          absencesByDay[dayKey].sort((a, b) => moment(a.from).valueOf() - moment(b.from).valueOf());
        });
      }

      Object.entries(absencesByStartDate).forEach(([startDate, absences]) => {
        (absences as PersonAbsence[]).forEach((ab: PersonAbsence) => {
          const isDeleted = deletedAbsenceKeys.some(
            (k) => ('id' in k && (k as any).id === ab.id) || ('guid' in k && (k as any).guid === ab.guid)
          );
          const startMoment = moment(ab.from);
          const endMoment = moment(ab.to);
          let displayStart = startMoment.valueOf();
          let displayEnd = endMoment.valueOf();
          let title = formatScheduleTitle(ab, timelineScale);

          if (timelineScale === TimelineScale.MONTH) {
            title = ab.absence.shortName;
            displayStart = startMoment.startOf('day').valueOf();
            displayEnd = endMoment.endOf('day').valueOf();
            if (startMoment.isSame(endMoment, 'day')) {
              const singleDayKey = startMoment.format('YYYY-MM-DD');
              const dayAbsencesForThatDay = absencesByDay[singleDayKey] || [];
              if (dayAbsencesForThatDay.length === 1) {
                displayStart = startMoment.clone().startOf('day').add(2, 'hours').valueOf();
                displayEnd = startMoment.clone().startOf('day').add(22, 'hours').valueOf();
              }
            }

            let currentDate = startMoment.clone().startOf('day');
            const endDate = endMoment.clone().startOf('day');
            while (currentDate.isSameOrBefore(endDate)) {
              const dayKey = currentDate.format('YYYY-MM-DD');
              const dayAbsences = absencesByDay[dayKey] || [];
              const absIndexInDay = dayAbsences.findIndex((a) => a.from === ab.from && a.to === ab.to);
              if (absIndexInDay !== -1 && dayAbsences.length > 1) {
                const dayStart = currentDate.valueOf();
                const segmentWidth = (currentDate.endOf('day').valueOf() - dayStart) / dayAbsences.length;
                if (currentDate.isSame(startMoment, 'day')) {
                  displayStart = dayStart + absIndexInDay * segmentWidth;
                }
                if (currentDate.isSame(endMoment, 'day')) {
                  displayEnd = dayStart + (absIndexInDay + 1) * segmentWidth;
                }
              }
              currentDate.add(1, 'day');
            }
          }

          let absenceClassName = 'filled-cell absence-item';
          if (isDeleted) absenceClassName += ' deleted-absence';
          if (!isDeleted && ab.absence.shouldRemoveShift) {
            absenceClassName += ' opaque';
          } else if (!isDeleted) {
            absenceClassName += ' semi-transparent';
          }

          const itemProps: any = {
            style: {
              backgroundColor: isDeleted ? 'gray' : ab.absence.shouldRemoveShift ? '#ff9999' : 'rgba(255, 153, 153, 0.5)',
              borderRadius: 4,
              color: '#000',
              top: 0
            }
          };
          if (timelineScale === TimelineScale.MONTH) {
            itemProps.style.cursor = 'pointer';
            itemProps['data-original-from'] = ab.from;
            itemProps['data-original-to'] = ab.to;
            itemProps['data-absence-id'] = (ab as any).id || (ab as any).guid;
          }

          absenceItems.push({
            id: `${emp.id}-absence-${ab.from}`,
            group: emp.id,
            className: absenceClassName,
            title,
            start_time: displayStart,
            end_time: displayEnd,
            itemProps
          });
        });
      });
    });
  });

  return absenceItems;
};


